// The ebook's sample page (and a post's HTML) shown in the Publish preview. The page's policy forbids inline styles and
// requires Trusted Types for markup, and a frame's document would inherit both, so the sample isn't loaded into a
// frame: its markup (which the compile worker wrote, from the writer's own text) is read here into a small tree and
// built with `createElement` inside a shadow root, keeping only the elements and attributes a book page uses, and its
// style sheet is adopted as a constructed sheet (as the editor's is). Nothing in it can run.

export interface MarkupNode { tag: string; attributes: Record<string, string>; children: (MarkupNode | string)[] }

const VOID = new Set(['meta', 'link', 'br', 'hr', 'img', 'input', 'col', 'wbr', 'source', 'area', 'base', 'embed', 'param', 'track']);
const RAW = new Set(['style', 'script', 'title', 'textarea']);
const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', shy: '­', mdash: '—', ndash: '–', hellip: '…', lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”' };

function decode(s: string): string {
  return s.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|[A-Za-z]+);/g, (m, e: string) => {
    if (e[0] === '#') {
      const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : m;
    }
    return ENTITIES[e] ?? m;
  });
}

/** Reads (X)HTML leniently: void elements, unclosed ones closed by their parent's end, raw text in style/script. */
export function parseMarkup(source: string): MarkupNode {
  const root: MarkupNode = { tag: '#root', attributes: {}, children: [] };
  const stack: MarkupNode[] = [root];
  let i = 0;
  const top = () => stack[stack.length - 1];
  while (i < source.length) {
    const lt = source.indexOf('<', i);
    if (lt < 0) { top().children.push(decode(source.slice(i))); break; }
    if (lt > i) top().children.push(decode(source.slice(i, lt)));
    if (source.startsWith('<!--', lt)) { const end = source.indexOf('-->', lt + 4); i = end < 0 ? source.length : end + 3; continue; }
    if (source.startsWith('<!', lt) || source.startsWith('<?', lt)) { const end = source.indexOf('>', lt); i = end < 0 ? source.length : end + 1; continue; }
    const end = source.indexOf('>', lt);
    if (end < 0) { top().children.push(decode(source.slice(lt))); break; }
    const inner = source.slice(lt + 1, end);
    i = end + 1;
    if (inner.startsWith('/')) {
      const name = inner.slice(1).trim().toLowerCase();
      const at = stack.map((n) => n.tag).lastIndexOf(name);
      if (at > 0) stack.length = at;
      continue;
    }
    const m = /^([A-Za-z][\w:.-]*)([\s\S]*?)(\/?)\s*$/.exec(inner);
    if (!m) { top().children.push(decode(`<${inner}>`)); continue; }
    const tag = m[1].toLowerCase();
    const attributes: Record<string, string> = {};
    for (const a of m[2].matchAll(/([^\s=/]+)(?:\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g)) attributes[a[1].toLowerCase()] = decode(a[3] ?? a[4] ?? a[5] ?? '');
    const node: MarkupNode = { tag, attributes, children: [] };
    top().children.push(node);
    if (RAW.has(tag)) {
      const close = source.toLowerCase().indexOf(`</${tag}`, i);
      node.children.push(source.slice(i, close < 0 ? source.length : close));
      i = close < 0 ? source.length : source.indexOf('>', close) + 1 || source.length;
      continue;
    }
    if (!m[3] && !VOID.has(tag)) stack.push(node);
  }
  return root;
}

/** The elements a book page is made of; anything else keeps its content without its tag. */
const KEPT = new Set([
  'section', 'article', 'div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'em', 'i', 'strong', 'b', 'u', 's', 'span', 'br', 'hr', 'blockquote',
  'ul', 'ol', 'li', 'sup', 'sub', 'small', 'nav', 'aside', 'header', 'footer', 'figure', 'figcaption', 'cite', 'abbr', 'code', 'pre', 'a',
]);
const DROPPED = new Set(['script', 'style', 'title', 'head', 'meta', 'link', 'iframe', 'object', 'embed', 'img', 'svg', 'math', 'form', 'input', 'button', 'textarea', 'select', 'template']);

function build(doc: Document, node: MarkupNode | string, into: Node) {
  if (typeof node === 'string') { into.appendChild(doc.createTextNode(node)); return; }
  if (DROPPED.has(node.tag)) return;
  if (!KEPT.has(node.tag)) { for (const c of node.children) build(doc, c, into); return; }
  const el = doc.createElement(node.tag);
  for (const [k, v] of Object.entries(node.attributes)) {
    if (k === 'class' || k === 'id' || k === 'lang' || k === 'dir' || k === 'title' || k === 'role' || k.startsWith('aria-')) el.setAttribute(k, v);
    else if (k === 'epub:type') el.setAttribute('data-epub-type', v);
    else if (k === 'href' && node.tag === 'a' && v.startsWith('#')) el.setAttribute('data-href', v); // links go nowhere in a sample
  }
  for (const c of node.children) build(doc, c, el);
  into.appendChild(el);
}

function find(node: MarkupNode, tag: string): MarkupNode | null {
  for (const c of node.children) {
    if (typeof c === 'string') continue;
    if (c.tag === tag) return c;
    const inner = find(c, tag);
    if (inner) return inner;
  }
  return null;
}

function styles(node: MarkupNode, out: string[] = []): string[] {
  for (const c of node.children) {
    if (typeof c === 'string') continue;
    if (c.tag === 'style') out.push(c.children.filter((x): x is string => typeof x === 'string').join(''));
    else styles(c, out);
  }
  return out;
}

/** The page's style sheet with `html` and `body` rules pointed at the wrappers that stand for them. */
export function scopedCSS(css: string): string {
  return css.replace(/(^|[},]\s*)(html|body)(?=[\s{,:.[>+~])/g, (_m, pre: string, el: string) => `${pre}.sample-${el}`);
}

/** Draws the page into a shadow root: `.sample-html > .sample-body > …`, with its own sheet. */
export function renderSample(host: HTMLElement, source: string): void {
  const tree = parseMarkup(source);
  const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(`:host { display: block; } .sample-html { min-height: 100%; }\n${scopedCSS(styles(tree).join('\n'))}`);
  shadow.adoptedStyleSheets = [sheet];
  shadow.replaceChildren();
  const doc = host.ownerDocument;
  const html = doc.createElement('div');
  html.className = 'sample-html';
  const body = doc.createElement('div');
  body.className = 'sample-body';
  const lang = find(tree, 'html')?.attributes.lang;
  if (lang) html.setAttribute('lang', lang);
  html.appendChild(body);
  const content = find(tree, 'body') ?? tree;
  for (const c of content.children) build(doc, c, body);
  shadow.appendChild(html);
}
